import {
  Assignment,
  isLocationAssignment,
  isTenantAssignment,
} from './Assignment';
import { OnboardingStatus, LocationRole, TenantRole } from './types';

// Phone number structure for internationalization
export interface PhoneNumber {
  countryCode: string;
  number: string;
}

/**
 * A single verified-email entry in a user's multi-email set.
 * Additive — absent on all legacy user docs; readers fall back gracefully
 * via `verifiedEmails ?? []`.
 */
export interface VerifiedEmailEntry {
  /** The email address (lowercased, canonical form). */
  readonly email: string;
  /** When the email was confirmed verified on this account. */
  readonly verifiedAt: Date;
  /**
   * Firebase provider that attested the verification — e.g. `'password'`,
   * `'google.com'`, `'apple.com'`. Stored for forensics; not used for
   * authorization decisions.
   */
  readonly providerId: string;
}

export interface IUser {
  // Core user properties
  readonly uid: string;
  readonly email: string;
  readonly displayName?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly phoneNumber?: PhoneNumber;
  /**
   * True when the user's `phoneNumber` has been proven via a Firebase phone
   * provider (SMS code). Optional + additive — absent/false on every legacy
   * doc, so no existing reader changes behavior. Server-trusted only: it is
   * written from the verified Firebase token, never from a client field.
   */
  readonly phoneVerified?: boolean;
  readonly photoURL?: string | null;
  readonly emailVerified: boolean;

  /**
   * Workstream E — multi-email set. Full records with timestamps and provider
   * attestation. Absent on all legacy docs; additive — every existing reader
   * of `user.email` is unaffected. Server-trusted only (never accepted from
   * client bodies).
   */
  readonly verifiedEmails?: VerifiedEmailEntry[];
  /**
   * Denormalized, lowercased email addresses from `verifiedEmails` — kept in
   * sync by the server and used for Firestore `array-contains` queries.
   * Absent on legacy docs; falls back to `[]`.
   */
  readonly verifiedEmailAddresses?: string[];

  /**
   * Unified scope-aware assignments (tenant-scoped and restaurant-scoped).
   * Source of truth for management-app role authorization.
   * Syncs into Firebase custom claims via IClaimsService on change.
   */
  readonly assignments: Assignment[];

  // User status and metadata
  readonly isActive: boolean;
  readonly createdAt: Date;
  readonly lastLogin?: Date;
  readonly updatedAt: Date;

  // Limbo tracking — set when last active assignment is revoked; cleared on grant
  readonly limboStartedAt?: Date | null;
  readonly limboWarningEmailSentAt?: Date | null;

  // Onboarding lifecycle
  readonly onboardingStatus: OnboardingStatus;
  readonly onboardingApplicationId?: string;  // Supabase application ID
  readonly onboardingStartedAt?: Date;
  readonly onboardingSubmittedAt?: Date;
  readonly onboardingCompletedAt?: Date;
}

export class User implements IUser {
  readonly uid: string;
  readonly email: string;
  readonly displayName?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly phoneNumber?: PhoneNumber;
  readonly phoneVerified?: boolean;
  readonly photoURL?: string | null;
  readonly emailVerified: boolean;
  readonly verifiedEmails?: VerifiedEmailEntry[];
  readonly verifiedEmailAddresses?: string[];
  readonly assignments: Assignment[];
  readonly isActive: boolean;
  readonly createdAt: Date;
  readonly lastLogin?: Date;
  readonly updatedAt: Date;
  readonly limboStartedAt?: Date | null;
  readonly limboWarningEmailSentAt?: Date | null;
  readonly onboardingStatus: OnboardingStatus;
  readonly onboardingApplicationId?: string;
  readonly onboardingStartedAt?: Date;
  readonly onboardingSubmittedAt?: Date;
  readonly onboardingCompletedAt?: Date;

  constructor(data: IUser) {
    this.uid = data.uid;
    this.email = data.email;
    this.displayName = data.displayName;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.phoneNumber = data.phoneNumber;
    this.phoneVerified = data.phoneVerified;
    this.photoURL = data.photoURL;
    this.emailVerified = data.emailVerified ?? false;
    this.verifiedEmails = data.verifiedEmails;
    this.verifiedEmailAddresses = data.verifiedEmailAddresses;
    this.assignments = data.assignments ?? [];
    this.isActive = data.isActive ?? false;
    this.createdAt = data.createdAt || new Date();
    this.lastLogin = data.lastLogin;
    this.updatedAt = data.updatedAt || new Date();
    this.limboStartedAt = data.limboStartedAt;
    this.limboWarningEmailSentAt = data.limboWarningEmailSentAt;
    this.onboardingStatus = data.onboardingStatus ?? 'not_started';
    this.onboardingApplicationId = data.onboardingApplicationId;
    this.onboardingStartedAt = data.onboardingStartedAt;
    this.onboardingSubmittedAt = data.onboardingSubmittedAt;
    this.onboardingCompletedAt = data.onboardingCompletedAt;
  }

  // --- General helpers ---------------------------------------------------

  getDisplayName(): string {
    return this.displayName || this.email || 'Unknown User';
  }

  activeAssignments(): Assignment[] {
    return this.assignments.filter((a) => a.isActive);
  }

  // --- Tenant-scope helpers ----------------------------------------------

  /**
   * Returns the active tenant-scoped role at this tenant, or null.
   * Does NOT consider restaurant-scoped grants.
   */
  getTenantRole(tenantDocId: string): TenantRole | null {
    const match = this.assignments.find(
      (a) => a.isActive && isTenantAssignment(a) && a.tenantDocId === tenantDocId,
    );
    return match ? (match.role as TenantRole) : null;
  }

  /**
   * True iff the user holds ANY active assignment (tenant- or restaurant-scoped)
   * within this tenant.
   */
  hasAnyAccessToTenant(tenantDocId: string): boolean {
    return this.assignments.some(
      (a) => a.isActive && a.tenantDocId === tenantDocId,
    );
  }

  /**
   * Returns the distinct tenantDocIds the user holds any active grant within.
   */
  accessibleTenantDocIds(): string[] {
    const set = new Set<string>();
    for (const a of this.assignments) {
      if (a.isActive) set.add(a.tenantDocId);
    }
    return [...set];
  }

  // --- Restaurant-scope helpers ------------------------------------------

  /**
   * Returns the active restaurant-scoped role for this specific restaurant,
   * or null. Does NOT fall back to tenant role — use AuthorizationService
   * for the full inherited computation.
   */
  getLocationRole(
    tenantDocId: string,
    locationDocId: string,
  ): LocationRole | null {
    const match = this.assignments.find(
      (a) =>
        a.isActive &&
        isLocationAssignment(a) &&
        a.tenantDocId === tenantDocId &&
        a.restaurantDocId === locationDocId,
    );
    return match ? (match.role as LocationRole) : null;
  }

  /**
   * Returns the distinct restaurantDocIds the user holds active
   * restaurant-scoped grants for within this tenant.
   */
  accessibleLocationDocIds(tenantDocId: string): string[] {
    const set = new Set<string>();
    for (const a of this.assignments) {
      if (
        a.isActive &&
        isLocationAssignment(a) &&
        a.tenantDocId === tenantDocId
      ) {
        set.add(a.restaurantDocId);
      }
    }
    return [...set];
  }

  // --- Onboarding helpers ------------------------------------------------

  /**
   * A user needs onboarding iff their email is verified but onboarding has
   * not completed. The `isActive` flag trails `onboardingStatus === 'completed'`
   * and is only set server-side during provisioning.
   */
  needsOnboarding(): boolean {
    return this.emailVerified && this.onboardingStatus !== 'completed';
  }

  isAwaitingApproval(): boolean {
    return this.onboardingStatus === 'submitted';
  }

  canAccessPlatform(): boolean {
    return this.isActive && this.onboardingStatus === 'completed';
  }

  wasDeclined(): boolean {
    return this.onboardingStatus === 'declined';
  }

  // --- Serialization -----------------------------------------------------

  toJSON(): IUser {
    return {
      uid: this.uid,
      email: this.email,
      displayName: this.displayName,
      firstName: this.firstName,
      lastName: this.lastName,
      phoneNumber: this.phoneNumber,
      phoneVerified: this.phoneVerified,
      photoURL: this.photoURL,
      emailVerified: this.emailVerified,
      verifiedEmails: this.verifiedEmails,
      verifiedEmailAddresses: this.verifiedEmailAddresses,
      assignments: this.assignments,
      isActive: this.isActive,
      createdAt: this.createdAt,
      lastLogin: this.lastLogin,
      updatedAt: this.updatedAt,
      limboStartedAt: this.limboStartedAt,
      limboWarningEmailSentAt: this.limboWarningEmailSentAt,
      onboardingStatus: this.onboardingStatus,
      onboardingApplicationId: this.onboardingApplicationId,
      onboardingStartedAt: this.onboardingStartedAt,
      onboardingSubmittedAt: this.onboardingSubmittedAt,
      onboardingCompletedAt: this.onboardingCompletedAt,
    };
  }

  static fromJSON(data: Record<string, any>): User {
    if (!data.uid || typeof data.uid !== 'string') {
      throw new Error('User.fromJSON: UID is missing or invalid.');
    }
    if (!data.email || typeof data.email !== 'string') {
      throw new Error('User.fromJSON: Email is missing or invalid.');
    }

    const toDate = (dateValue: any): Date | undefined => {
      if (!dateValue) return undefined;
      if (dateValue.toDate) return dateValue.toDate(); // Firestore Timestamp
      if (typeof dateValue === 'string') return new Date(dateValue);
      return dateValue as Date;
    };

    const assignments = ((data.assignments as any[]) || []).map(
      (raw: any): Assignment => {
        // Derive status: explicit field → fall back from isActive for legacy docs
        const status = raw.status ?? (raw.isActive !== false ? 'active' : 'revoked');
        return {
          scope: raw.scope,
          tenantDocId: raw.tenantDocId,
          restaurantDocId: raw.restaurantDocId,
          role: raw.role,
          status,
          isActive: status === 'active',
          grantedByUid: raw.grantedByUid,
          grantedAt: toDate(raw.grantedAt)!,
          updatedAt: toDate(raw.updatedAt) || new Date(),
          // PROVENANCE MUST SURVIVE THE READ. This mapper is an explicit field
          // list, so anything omitted is dropped silently on every read — and
          // `grantSource` was omitted. `effectiveAssignmentSource()` normalises
          // an absent value to 'manual', so every assignment read through here
          // reported itself as operator-owned: the JobTitle reconciler could not
          // recognise even its own `job_title` grants, and the admin API's
          // provenance column was wrong for every row.
          //
          // Spread conditionally, never `grantSource: raw.grantSource` — writing
          // an explicit `undefined` would make a legacy document (which
          // genuinely has no value) indistinguishable from a stamped one under
          // a deep-equality check, and Firestore rejects undefined on write.
          ...(raw.grantSource ? { grantSource: raw.grantSource } : {}),
          ...(raw.supersedes
            ? {
                supersedes: {
                  role: raw.supersedes.role,
                  grantSource: raw.supersedes.grantSource,
                  grantedByUid: raw.supersedes.grantedByUid,
                  grantedAt: toDate(raw.supersedes.grantedAt)!,
                },
              }
            : {}),
        };
      },
    );

    // Parse verifiedEmails array — absent on legacy docs, so default to undefined
    let verifiedEmails: VerifiedEmailEntry[] | undefined;
    if (Array.isArray(data.verifiedEmails) && data.verifiedEmails.length > 0) {
      verifiedEmails = (data.verifiedEmails as any[]).map((entry: any): VerifiedEmailEntry => ({
        email: String(entry.email).toLowerCase(),
        verifiedAt: toDate(entry.verifiedAt) ?? new Date(),
        providerId: String(entry.providerId ?? ''),
      }));
    }

    // Parse denormalized address list — absent on legacy docs
    const verifiedEmailAddresses: string[] | undefined = Array.isArray(data.verifiedEmailAddresses)
      ? (data.verifiedEmailAddresses as string[]).map((e) => String(e).toLowerCase())
      : undefined;

    return new User({
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      firstName: data.firstName as string | undefined,
      lastName: data.lastName as string | undefined,
      phoneNumber: data.phoneNumber as PhoneNumber | undefined,
      phoneVerified: data.phoneVerified as boolean | undefined,
      photoURL: data.photoURL as string | null | undefined,
      emailVerified: data.emailVerified as boolean | undefined ?? false,
      verifiedEmails,
      verifiedEmailAddresses,
      isActive: data.isActive as boolean | undefined ?? false,
      createdAt: toDate(data.createdAt) || new Date(),
      lastLogin: toDate(data.lastLogin),
      updatedAt: toDate(data.updatedAt) || new Date(),
      limboStartedAt: toDate(data.limboStartedAt) ?? null,
      limboWarningEmailSentAt: toDate(data.limboWarningEmailSentAt) ?? null,
      assignments,
      onboardingStatus: data.onboardingStatus as OnboardingStatus | undefined ?? 'not_started',
      onboardingApplicationId: data.onboardingApplicationId as string | undefined,
      onboardingStartedAt: toDate(data.onboardingStartedAt),
      onboardingSubmittedAt: toDate(data.onboardingSubmittedAt),
      onboardingCompletedAt: toDate(data.onboardingCompletedAt),
    });
  }
}
